import {
  Directive,
  HostListener,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  Optional,
  OnInit,
  DestroyRef, inject
} from '@angular/core';
import {debounceTime} from "rxjs";
import {NgControl} from "@angular/forms";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Directive({
  selector: '[validator-date]',
  standalone: true
})

export class ValidatorDateDirective implements OnInit {

  protected dateValue = '';
  private destroy = inject(DestroyRef)

  @Output() dateChange: EventEmitter<null | Date> = new EventEmitter<null | Date>();

  @Input('date-value') modelValue: any;

  @Input() isStartDate: boolean = false;

  constructor(
    private el: ElementRef,
    @Optional() private controlDir: NgControl
  ) {}

  ngOnInit(): void {
    if (this.controlDir?.control) {
      this.controlDir.control.valueChanges
        .pipe(debounceTime(0))
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((val) => {
          const input = this.el.nativeElement as HTMLInputElement;
          const formatted = this.formatAndValidateDate(val || '');
          if (input.value !== formatted) {
            input.value = formatted;
            this.dateValue = formatted;
            this.controlDir.control?.setValue(formatted, {emitEvent: false});
            this.dateChange.emit(this.tryParseDate(formatted));
          }
        });
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') ?? '';
    const formatted = this.formatAndValidateDate(pastedText);

    const input = this.el.nativeElement as HTMLInputElement;
    input.value = formatted;
    this.dateValue = formatted;

    this.dateChange.emit(this.tryParseDate(formatted));
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (input.value.length >= 10 && event.key !== 'Backspace') {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace') {
      const caretStart = input.selectionStart ?? 0;
      const caretEnd = input.selectionEnd ?? 0;

      if (input.value[caretStart - 1] === '.' && !isNaN(parseInt(input.value[caretStart - 2]))) {
        const valueBeforeCaret = input.value.slice(0, caretStart - 1);
        const valueAfterCaret = input.value.slice(caretEnd);
        input.value = valueBeforeCaret + valueAfterCaret;
        event.preventDefault();
        input.setSelectionRange(caretStart - 1, caretStart - 1);
      }
    }
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    setTimeout(() => {
      const input = this.el.nativeElement as HTMLInputElement;
      const formatted = this.formatAndValidateDate(input.value);
      if (input.value !== formatted) {
        input.value = formatted;
        this.dateValue = formatted;
      }
    }, 0);
  }

  private formatAndValidateDate(raw: string): string {
    let value = raw?.replace(/[^\d]/g, '').slice(0, 8);

    if (value.length >= 3) {
      value = value.slice(0, 2) + '.' + value.slice(2);
    }
    if (value.length >= 6) {
      value = value.slice(0, 5) + '.' + value.slice(5);
    }

    const day = parseInt(value.slice(0, 2), 10);
    if (!isNaN(day)) {
      if (day > 31) value = '31' + value.slice(2);
      if (day < 1 && value.length >= 2) value = '01' + value.slice(2);
    }

    const month = parseInt(value.slice(3, 5), 10);
    if (!isNaN(month)) {
      if (month > 12) value = value.slice(0, 3) + '12' + value.slice(5);
      if (month < 1 && value.length >= 5) value = value.slice(0, 3) + '01' + value.slice(5);
    }

    return value;
  }

  private tryParseDate(value: string): Date | null {
    const [day, month, year] = value.split('.');
    if (day && month && year && year.length === 4) {
      const parsed = new Date(+year, +month - 1, +day);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }
}

